(ns minorodata.components.transformations.search
  (:require ["lucide-react" :refer [ArrowRight Search X]]
            [applied-science.js-interop :as js-interop]
            [clojure.string :as str]
            [goog.functions :refer [debounce]]
            [minorodata.app.handlers :refer [dispatch dispatch-sync]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.app.utils :as utils]
            [minorodata.components.infinite-scroll.core :as c.infinite-scroll]
            [minorodata.components.shortcut.core :as c.shortcut]
            [minorodata.components.spinner.core :as c.spinner]
            [minorodata.components.transformations.utils :refer [transform-edit-sql-button
                                                                 transform-more-options-button]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [ui.button :refer [button]]
            [ui.date-time :refer [date-tooltip]]
            [ui.datepicker :refer [datetime-picker]]
            [ui.dialog :refer [dialog-close-x dialog-content dialog-root
                               dialog-title]]
            [ui.fields :refer [checkbox input select]]
            [ui.tabs :refer [tabs]]
            [ui.typography :as typography]))


(defn initial-search []
  (dispatch-sync [:transformations.events/do-search]))


(defn apply-search [prop value]
  (dispatch [:transformations-search/update {prop value}])
  (dispatch [:transformations.events/do-search]))


(defn- search-performed-capture [{:keys [term search-filter type]} new-term]
  (when (and (not (str/blank? new-term))
             (not= (str/trim new-term) (some-> term str/trim)))
    {:event (:transform/search-performed posthog-events/events)
     :props {:scope (or search-filter "titles")
             :type  (name (or type :transform))}}))


(def term-debounce-timeout 600)

(def apply-search-term-debounce
  (debounce
   (fn [value search]
     (when-let [capture (search-performed-capture search value)]
       (dispatch [:posthog/capture capture]))
     (apply-search :transformations-search/term value))
   term-debounce-timeout))


(defn apply-search-term [event search]
  (let [value (js-interop/get-in event [:target :value])]
    (apply-search-term-debounce value search)))


(defn reset-search-term [input]
  (js-interop/assoc! input :value "")
  (apply-search :transformations-search/term ""))


(defn navigate-to-result [id]
  (dispatch [:transformations.events/navigate-to-search-result {:type :group :id id}]))


(defn update-search-filter [new-filter]
  (dispatch [:transformations-search/update {:transformations-search/search-filter new-filter}])
  (dispatch [:transformations.events/do-search]))

(defn search-results-item [{:keys [id group-id master-id type name description updated-at updated-by close-search-dialog search-type]}]
  [:div {:class "relative border-b"}
   [:div {:data-cy  :transform-search__result-link}
    [:div {:class "px-4 py-2"}
     (let [transform {:id id :group-id group-id :master-id master-id}]
       [:div {:class "absolute right-4 bottom-2"}
        [:div {:class "flex gap-2"}
         (when (= :transform type)
           [:<>
            [transform-edit-sql-button {:transform transform
                                        :size      20
                                        :on-click  close-search-dialog}]
            [transform-more-options-button {:transform       transform
                                            :size            20
                                            :exclude-options #{:move-transform :delete :show-history}
                                            :on-option-click close-search-dialog}]])
         [:div {:title    "Go to group"
                :on-click #(do
                             (navigate-to-result (if (= :group type) id group-id))
                             (close-search-dialog))}
          [:> ArrowRight {:size  20
                          :class "cursor-pointer hover:text-kleene-secondary-v2 transition-colors"}]]]])

     (when (= :both search-type)
       [:span {:class "uppercase text-xs text-kleene-text-secondary mb-1"}
        (if (= type :group) "Group" "Transform")])

     [typography/h4 {:data-cy (str "transform-search__result-title__" group-id)}
      name]

     (when (some? description)
       [typography/p {:class "text-kleene-text-secondary"}
        description])

     [typography/p {:class "text-kleene-text-secondary text-xs"}
      "Updated - "
      [:span {:class "text-kleene-text"}
       [date-tooltip {:date updated-at}]]
      " By - "
      [:span {:class "text-kleene-text"}
       updated-by]]]]])


(defn search-results-list []
  (let [searching?     (re-posh/subscribe [:mold-http/loading? :transformations.api/load-search-results])
        search-results (re-posh/subscribe [:transformations.subs/search-results])
        type           (re-posh/subscribe [:transformations-search/prop :transformations-search/type])]

    (fn [{:keys [close-search-dialog]}]
      (let [{:keys [matched]} @search-results]
        [:div {:class   "flex flex-col"
               :data-cy :transform-search__result}
         (if (or (> (count matched) 0)
                 @searching?)
           (into
            [c.infinite-scroll/main
             {:component "div"
              :loader    [:div {:class "flex justify-center items-center p-10"}
                          [c.spinner/spinner-lucide {:size 48}]]
              :loading?  @searching?}]
            (for [item matched]
              ^{:key (:id item)}
              [:<>
               [search-results-item (merge item {:close-search-dialog close-search-dialog
                                                 :search-type         @type})]]))

           [:div {:class "mx-4 my-8"}
            [typography/p {:class "text-center"}
             "No transforms or groups found." [:br]
             "Try to change the search term or other filters."]])]))))

(defn checkbox-group [{:keys [items on-change selected]}]
  (let [selected-set (set selected)]
    [:div {:class "flex flex-col gap-3 mt-3"}
     (doall
      (for [{:keys [id name label]} items
            :let [checked? (contains? selected-set id)]]
        ^{:key id}
        [checkbox {:on-change #(on-change id %)
                   :checked checked?
                   :label label
                   :name name
                   :id id}]))]))

(defn status-filter []
  (let [status @(re-posh/subscribe [:transformations-search/prop :transformations-search/status])]
    [checkbox-group
     {:selected  status
      :items     [{:id :active :name "status-active" :label "Active"}
                  {:id :paused :name "status-paused" :label "Paused"}
                  {:id :unscheduled :name "status-unscheduled" :label "Unscheduled"}]
      :on-change (fn [id checked]
                   (let [value (if checked
                                 (conj status id)
                                 (utils/remove-elem status id))]
                     (apply-search :transformations-search/status value)))}]))

(defn scheduled-filter []
  (let [scheduled @(re-posh/subscribe [:transformations-search/prop :transformations-search/scheduled])]
    [:div {:class "mt-3"}
     [checkbox {:name      "scheduled"
                :label     "Transforms with schedule"
                :checked   scheduled
                :on-change #(apply-search :transformations-search/scheduled %)}]]))


(defn tags-filter []
  (let [tags @(re-posh/subscribe [:transformations-search/prop :transformations-search/tags])
        {:transformations-view/keys [transforms-tags]} @(re-posh/subscribe [:transformations-view/pull
                                                                            '[{:transformations-view/transforms-tags [*]}]])
        sorted-transforms-tags (sort-by :transform-tag/label transforms-tags)]
    [checkbox-group
     {:selected  tags
      :items     (map
                  (fn [{:transform-tag/keys [value label]}]
                    {:id value :name label :label label})
                  sorted-transforms-tags)
      :on-change (fn [id checked]
                   (let [value (if checked
                                 (conj tags id)
                                 (utils/remove-elem tags id))]
                     (apply-search :transformations-search/tags value)))}]))


(defn users-filter []
  (let [users     @(re-posh/subscribe [:transformations-search/prop :transformations-search/updated-by])
        all-users @(re-posh/subscribe [:searchable-user/pull-many])]
    [checkbox-group
     {:selected  users
      :items     (map
                  (fn [{:searchable-user/keys [id username]}]
                    {:id id :name id :label username})
                  all-users)
      :on-change (fn [id checked]
                   (let [value (if checked
                                 (conj users id)
                                 (utils/remove-elem users id))]
                     (apply-search :transformations-search/updated-by value)))}]))

(defn date-filter []
  (let [updated-at  @(re-posh/subscribe [:transformations-search/prop :transformations-search/updated-at])
        query-dates (if (not-empty updated-at)
                      [(:from updated-at) (:to updated-at)]
                      [])]
    [:div {:class "mt-2"}
     [datetime-picker {:value     query-dates
                       :on-change (fn [[new-date-from new-date-to]]
                                    (if (nil? new-date-from)
                                     (apply-search :transformations-search/updated-at {})
                                     (let [value {:from new-date-from :to new-date-to}]
                                       (apply-search :transformations-search/updated-at value))))}]]))


(defn sticky-header [children]
  [:header {:class "pt-1 pb-2 bg-kleene-background sticky top-0 -mx-px px-px border-b"}
   children])

(defn search-filters []
  [:div {:class "relative px-4 pb-2 flex flex-col gap-6"}
   [:section
    [sticky-header
     [typography/label "Manual schedule"]]
    [scheduled-filter]]

   [:section
    [sticky-header
     [typography/label "Status"]]
    [status-filter]]

   [:section
    [sticky-header
     [typography/label "Tags"]]
    [tags-filter]]

   [:section
    [sticky-header
     [typography/label "Updated by"]]
    [users-filter]]

   [:section
    [sticky-header
     [typography/label "Updated at"]]
    [date-filter]]])

(defn search-term-input []
  (let [input-ref     (reagent/atom nil)
        term          (re-posh/subscribe [:transformations-search/prop :transformations-search/term])
        search-filter (re-posh/subscribe [:transformations-search/prop :transformations-search/search-filter])
        type          (re-posh/subscribe [:transformations-search/prop :transformations-search/type])]

    (fn []
      [:div
       [:div {:class "grid gap-4 grid-cols-12"}
        [:div {:class "col-span-10"}
         [:div {:class "relative"}
          [input {:placeholder   "Start typing to search by group or transform name or description or SQL"
                  :data-cy       "transform-search-input"
                  :default-value (or @term "")
                  :input-props   {:class     "py-4 px-10"
                                  :ref       #(reset! input-ref %)}
                  :auto-focus    true
                  :icon          Search
                  :on-change     #(apply-search-term % {:term          @term
                                                         :search-filter @search-filter
                                                         :type          @type})}]
          [:div {:class "absolute right-2 top-1/2 -translate-y-1/2 leading-zero"}
           [button {:size     :icon
                    :variant  :secondary
                    :icon     X
                    :rounded  true
                    :on-click #(reset-search-term @input-ref)}]]]]

        [:div {:class "col-span-2"}
         [select {:value     (or @search-filter "titles")
                  :label     "Search in"
                  :name      "search-filtering-option"
                  :id        "search-filtering-option"
                  :native    false
                  :on-change update-search-filter

                  :label-props {:class "block"}
                  :class       "-mt-[3px]"
                  :options
                  [{:value "everything" :text "Everything"}
                   {:value "titles" :text "Name"}
                   {:value "text" :text "SQL"}]}]]]])))


(defn search-tabs []
  (let [type @(re-posh/subscribe [:transformations-search/prop :transformations-search/type])]
    [tabs {:full-width      true
           :variant         :underlined
           :value           (or type :transform)
           :on-value-change #(apply-search :transformations-search/type (keyword %))
           :tabs
           [{:value :both :label "All"}
            {:label "Groups" :value :group :data-cy :filter-groups}
            {:label "Transforms" :value :transform}]}]))

(defn search-dialog [{:keys [open? close-search-dialog]}]
  [dialog-root {:open           open?
                :on-open-change (fn [new-open]
                                  (if new-open
                                    (initial-search)
                                    (close-search-dialog)))}

   [dialog-content {:size  :large
                    :class "overflow-hidden flex flex-col gap-4"
                    :style {:height "90vh"}}
    [dialog-title
     [search-term-input]]

    [:div {:class "grid grid-cols-12 gap-1 overflow-hidden"}
     [:div {:class "col-span-8 flex flex-col items-stretch p-1 overflow-hidden"}
      [search-tabs]
      [:div {:class "overflow-y-auto"}
       [search-results-list
        {:close-search-dialog close-search-dialog}]]]
     [:div {:class "col-span-4 overflow-y-auto"}
      [search-filters]]]

    [dialog-close-x]]])

(defn search-button []
  (let [prod-environment? @(re-posh/subscribe [:environments.subs/production-active?])

        open #(dispatch-sync [:app.events/navigate-query {:search true}])]
    (when prod-environment?
      [button {:icon     Search
               :data-cy  :transform-search__toggle
               :variant  :secondary
               :on-click open}
       [:span {:class "flex items-center"}
        "Search"
        [c.shortcut/shortcut
         {:inline    true
          :title     "Press / to open search window"
          :value     "/"
          :shortcuts [{:shortcut "/" :handler (when prod-environment? open) :options {:useKey true}}]
          :classes   {:kbd "border-0! bg-kleene-selected-background h-5 w-5"}}]]])))



(defn search-modal []
  (reagent/create-class
   {:display-name
    "search-main"

    :constructor
    (fn []
      (dispatch [:transformations-search/add]))

    :component-will-unmount
    (fn []
      (dispatch-sync [:transformations-search/delete]))

    :render
    (fn [this]
      (let [props (reagent/props this)
            {:app-route/keys [query]} (:route props)
            open? (:search query)]
        [search-dialog
         {:open?               open?
          :close-search-dialog #(js/setTimeout (fn [] (dispatch [:app.events/navigate-query {:search nil}]))
                                               1)}]))}))
